import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { CarCard } from '@/components/cars/CarCard';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

export function RecentlySoldSection() {
  const { t } = useI18n();

  // Fetch the last 4 sold cars (FIFO - most recently sold first)
  const { data: soldCars, isLoading } = useQuery({
    queryKey: ['sold-cars'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cars')
        .select(`
          *,
          car_images(storage_path, storage_path_avif, storage_path_webp, is_primary, position)
        `)
        .eq('status', 'sold')
        .order('sold_at', { ascending: false })
        .limit(4);

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <section className="py-20 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Skeleton className="h-10 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-3xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!soldCars || soldCars.length === 0) {
    return null; // Don't show section if no sold cars
  }

  return (
    <section className="py-20 bg-card/50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t.recentlySold.title}
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            {t.recentlySold.subtitle}
          </p>
        </div>

        {/* Carousel for mobile, grid for desktop */}
        <div className="hidden lg:grid grid-cols-4 gap-6">
          {soldCars.map((car) => (
            <CarCard key={car.id} car={car} showSoldBadge />
          ))}
        </div>

        <div className="lg:hidden">
          <Carousel className="w-full">
            <CarouselContent>
              {soldCars.map((car) => (
                <CarouselItem key={car.id} className="md:basis-1/2">
                  <CarCard car={car} showSoldBadge />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        </div>
      </div>
    </section>
  );
}
